// Browser side of desktop sign-in (B4 seam, frozen UI contract: packages/contracts/src/desktop-auth.ts).
// The desktop app opens GET /api/v1/auth/authorize; the server redirects to
// /desktop/authorize?request=<opaque handle>, and this page lets the signed-in user allow or deny it.
// The handle is a public continuation reference, not a code, verifier or token. The renderer never
// invents `state`, never mints tokens, and opens only a callback that exactly matches the one
// apps/api/src/desktop-auth.ts builds for the registered redirect URI.
import type { ApiClient, DataEnvelope } from '../lib/api.ts';
import { isAbortError, isApiRequestError } from '../lib/api.ts';

// Mirrors of the contract constants (a test asserts they are identical). Kept local so the
// browser bundle does not load TypeBox at runtime for a few strings.
export const DESKTOP_CLIENT_ID = 'sidetone-desktop';
export const DESKTOP_CLIENT_NAME = 'Sidetone desktop';
export const DESKTOP_SCOPE = 'sidetone.desktop';
export const DESKTOP_REDIRECT_URI = 'io.brandcore.sidetone://auth/callback';
export const DESKTOP_ISSUER = 'https://sidet.one';
export const DESKTOP_AUTHORIZE_PATH = '/desktop/authorize';

const HANDLE = /^[A-Za-z0-9_-]{43}$/;
const CODE = /^sdc_[A-Za-z0-9_-]{43}$/;
const STATE = /^[A-Za-z0-9_-]{16,128}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/;
const CALLBACK_PREFIX = `${DESKTOP_REDIRECT_URI}?`;
const CALLBACK_KEYS = new Set(['code', 'error', 'state', 'iss']);
/** F4's deep-link parser refuses longer links (apps/desktop/src/main/deeplink.ts MAX_LINK). */
const MAX_CALLBACK = 1024;
/** Desktop codes live 60 s (apps/api/migrations/007_desktop_auth.sql); the page forgets the callback after that. */
export const CALLBACK_LIFETIME_MS = 60_000;
const MAX_TIMER = 2 ** 31 - 1;

export interface DesktopAuthorizationView {
  client_id: typeof DESKTOP_CLIENT_ID;
  client_name: typeof DESKTOP_CLIENT_NAME;
  scope: typeof DESKTOP_SCOPE;
  redirect_uri: typeof DESKTOP_REDIRECT_URI;
  tenant_id: string;
  expires_at: string;
}

export interface DesktopConfirmation {
  request: string;
  decision: 'allow' | 'deny';
  tenant_id: string;
}

/**
 * The continuation handle from the page's own query string. Exactly `?request=<43 base64url>`:
 * missing, malformed, encoded, duplicated or extra parameters are refused before any request.
 */
export function parseAuthorizeSearch(search: string): string | null {
  const match = /^\?request=([A-Za-z0-9_-]{43})$/.exec(search);
  return match?.[1] ?? null;
}

function plainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function exactKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  const own = Object.keys(value);
  return own.length === keys.length && keys.every(k => Object.hasOwn(value, k));
}

export type DetailsCheck =
  | { ok: true; view: DesktopAuthorizationView }
  | { ok: false; reason: 'invalid' | 'tenant_mismatch' | 'expired' };

/** Validates GET …/confirmation data against the frozen schema and the signed-in workspace. */
export function checkDetails(data: unknown, currentTenantId: string | null, now: number): DetailsCheck {
  if (!plainObject(data)) return { ok: false, reason: 'invalid' };
  if (!exactKeys(data, ['client_id', 'client_name', 'scope', 'redirect_uri', 'tenant_id', 'expires_at'])) return { ok: false, reason: 'invalid' };
  if (data.client_id !== DESKTOP_CLIENT_ID || data.client_name !== DESKTOP_CLIENT_NAME || data.scope !== DESKTOP_SCOPE || data.redirect_uri !== DESKTOP_REDIRECT_URI) {
    return { ok: false, reason: 'invalid' };
  }
  if (typeof data.tenant_id !== 'string' || !UUID.test(data.tenant_id)) return { ok: false, reason: 'invalid' };
  if (typeof data.expires_at !== 'string' || !ISO_DATE_TIME.test(data.expires_at) || !Number.isFinite(Date.parse(data.expires_at))) return { ok: false, reason: 'invalid' };
  // The server compares tenant IDs exactly; so does this page.
  if (!currentTenantId || data.tenant_id !== currentTenantId) return { ok: false, reason: 'tenant_mismatch' };
  if (Date.parse(data.expires_at) <= now) return { ok: false, reason: 'expired' };
  return { ok: true, view: data as unknown as DesktopAuthorizationView };
}

export type CallbackCheck = { ok: true; kind: 'code' | 'denied' } | { ok: false };

/**
 * Accepts only the callback B4 builds: literally the registered scheme, host and path (so no
 * userinfo, port or other path), no fragment, whitespace or controls, and a query of exactly
 * `code`+`state`+`iss` (allow) or `error=access_denied`+`state`+`iss` (deny), each key once and
 * unencoded, with the canonical issuer and the bounds F4's deep-link parser enforces.
 * Parsed by hand so the result never depends on a browser's handling of custom-scheme URLs.
 */
export function checkCallback(raw: unknown, decision: 'allow' | 'deny'): CallbackCheck {
  if (typeof raw !== 'string' || raw.length > MAX_CALLBACK || !raw.startsWith(CALLBACK_PREFIX)) return { ok: false };
  if (/[\u0000- \u007f-\u009f#\\]/.test(raw)) return { ok: false };
  const seen = new Map<string, string>();
  for (const part of raw.slice(CALLBACK_PREFIX.length).split('&')) {
    const eq = part.indexOf('=');
    const key = eq > 0 ? part.slice(0, eq) : '';
    if (!CALLBACK_KEYS.has(key) || seen.has(key)) return { ok: false };
    let value: string;
    try {
      value = decodeURIComponent(part.slice(eq + 1));
    } catch {
      return { ok: false };
    }
    seen.set(key, value);
  }
  if (seen.size !== 3) return { ok: false };
  const state = seen.get('state');
  if (state === undefined || !STATE.test(state) || seen.get('iss') !== DESKTOP_ISSUER) return { ok: false };
  if (decision === 'allow') {
    const code = seen.get('code');
    return code !== undefined && CODE.test(code) ? { ok: true, kind: 'code' } : { ok: false };
  }
  return seen.get('error') === 'access_denied' ? { ok: true, kind: 'denied' } : { ok: false };
}

// ---- Controller --------------------------------------------------------------------------------

export type AuthorizePhase =
  | 'loading'
  | 'ready' // details verified; Allow and Cancel enabled
  | 'reauth' // the server needs a fresh browser sign-in (session or verification code)
  | 'tenant_mismatch' // the workspace changed (another tab, or the server disagrees)
  | 'expired' // expired, already used or not recognized
  | 'unavailable' // the server does not offer desktop sign-in (404 / 503)
  | 'error' // a read or a refused decision; nothing was used
  | 'deciding'
  | 'opening' // a validated code callback was handed to the desktop app
  | 'denied' // the user cancelled and the desktop app was told
  | 'cancelled' // the user cancelled while Allow was in flight; nothing is handed to the desktop
  | 'unknown' // the decision's outcome is not known; it is never re-sent
  | 'invalid_response' // the server's answer did not match the registered shapes; nothing opened
  | 'superseded'; // the session or workspace changed while deciding

export interface AuthorizeSnapshot {
  phase: AuthorizePhase;
  view: DesktopAuthorizationView | null;
  decision: 'allow' | 'deny' | null;
  /** True while a validated callback is held in memory for the explicit "Open Sidetone" button. */
  canOpen: boolean;
  /** A decision was refused before it was used (a refreshed CSRF token): choose again. */
  retryNotice: boolean;
  /** Safe server fields for Technical details. Never a URL, handle, code or state. */
  diagnostic: { code: string; requestId: string | null } | null;
}

export interface AuthorizeControllerOptions {
  api: Pick<ApiClient, 'request'>;
  request: string;
  /** Opens the validated custom-scheme callback (window.location.assign in the browser). */
  open: (callback: string) => void;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

const INITIAL: AuthorizeSnapshot = { phase: 'loading', view: null, decision: null, canOpen: false, retryNotice: false, diagnostic: null };

export class DesktopAuthorizeController {
  private snapshot: AuthorizeSnapshot = INITIAL;
  private readonly listeners = new Set<() => void>();
  private key: string | null = null;
  private tenantId: string | null = null;
  private inflight: AbortController | null = null;
  private generation = 0;
  /** A decision was used (or may have been): this page never sends another one on its own. */
  private settled = false;
  /** Held only in memory; never rendered, logged or stored. */
  private callback: string | null = null;
  private expiryTimer: unknown = null;
  private forgetTimer: unknown = null;
  private readonly now: () => number;
  private readonly setTimer: (fn: () => void, ms: number) => unknown;
  private readonly clearTimer: (handle: unknown) => void;

  constructor(private readonly options: AuthorizeControllerOptions) {
    if (!HANDLE.test(options.request)) throw new TypeError('invalid request handle');
    this.now = options.now ?? Date.now;
    this.setTimer = options.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = options.clearTimer ?? (handle => clearTimeout(handle as ReturnType<typeof setTimeout>));
  }

  getSnapshot = (): AuthorizeSnapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  /**
   * The signed-in identity and workspace the page shows. A change discards in-flight details or a
   * decision's answer and, unless a decision was already used, reloads details for the new session.
   */
  setSession(userId: string | null, tenantId: string | null): void {
    const key = `${userId ?? ''}|${tenantId ?? ''}`;
    if (key === this.key) return;
    const deciding = this.snapshot.phase === 'deciding';
    this.key = key;
    this.tenantId = userId ? tenantId : null;
    this.cancel();
    if (this.settled) {
      // A code issued for the previous identity or workspace is never offered to this one.
      this.forget();
      return;
    }
    if (deciding) {
      // The request may already be used for the previous workspace: never hand the answer on.
      this.settle();
      this.set({ phase: 'superseded', view: null, retryNotice: false });
      return;
    }
    if (!userId || !tenantId) {
      this.set({ phase: 'reauth', view: null, retryNotice: false, diagnostic: null });
      return;
    }
    void this.load();
  }

  /**
   * Reads the request again. Offered after a failed read, a refused decision, a workspace change
   * or an unknown outcome: reading never uses the request, and the server refuses a used one.
   */
  recheck(): void {
    if (!this.tenantId) return;
    const phase = this.snapshot.phase;
    if (phase === 'unknown') this.settled = false;
    else if (this.settled || phase === 'deciding' || phase === 'loading') return;
    void this.load();
  }

  /** Cancel pressed while Allow is in flight: its answer, if any, is discarded and never opened. */
  abandon(): void {
    if (this.snapshot.phase !== 'deciding') return;
    this.cancel();
    this.settle();
    this.set({ phase: 'cancelled', view: null, retryNotice: false });
  }

  /** Stops timers and in-flight work. A later setSession (React StrictMode remount) starts over. */
  dispose(): void {
    this.cancel();
    this.key = null;
    if (this.snapshot.phase === 'deciding') {
      this.settle();
      this.set({ phase: 'superseded', view: null });
    }
    this.forget();
  }

  /** "Open Sidetone" again: only the callback this page received and validated, while it is usable. */
  openAgain(): void {
    const raw = this.callback;
    const decision = this.snapshot.decision;
    if (!raw || !decision || !checkCallback(raw, decision).ok) return;
    this.open(raw);
  }

  /** Drops the held callback (its code has expired or the session changed). */
  forget(): void {
    if (this.forgetTimer !== null) this.clearTimer(this.forgetTimer);
    this.forgetTimer = null;
    this.callback = null;
    if (this.snapshot.canOpen) this.set({ canOpen: false });
  }

  /** Allow or Cancel. One decision per request: repeated clicks are ignored and nothing is re-sent automatically. */
  async decide(decision: 'allow' | 'deny'): Promise<void> {
    const view = this.snapshot.view;
    if (this.settled || this.snapshot.phase !== 'ready' || !view || this.tenantId === null) return;
    this.cancel();
    if (Date.parse(view.expires_at) <= this.now()) {
      this.set({ phase: 'expired', view: null, retryNotice: false });
      return;
    }
    if (view.tenant_id !== this.tenantId) {
      this.set({ phase: 'tenant_mismatch', view: null, retryNotice: false });
      return;
    }
    const generation = this.generation;
    const controller = new AbortController();
    this.inflight = controller;
    this.set({ phase: 'deciding', decision, retryNotice: false, diagnostic: null });
    const body: DesktopConfirmation = { request: this.options.request, decision, tenant_id: view.tenant_id };
    let data: unknown;
    try {
      const env = await this.options.api.request<DataEnvelope<unknown>>('/auth/authorize/confirmation', { method: 'POST', body: JSON.stringify(body), signal: controller.signal });
      data = plainObject(env) ? env.data : undefined;
    } catch (error) {
      if (generation !== this.generation || isAbortError(error)) return;
      this.inflight = null;
      if (isApiRequestError(error) && error.outcomeUnknown) {
        // It may have been used. A single-use confirmation is never re-sent; recheck() reads first.
        this.settle();
        this.set({ phase: 'unknown', view: null, diagnostic: diagnosticFor(error) });
        return;
      }
      if (isApiRequestError(error) && error.code === 'csrf_invalid') {
        // Refused before the request was used. The shell refreshes the session; the user chooses again.
        this.set({ phase: 'ready', decision: null, retryNotice: true, diagnostic: diagnosticFor(error) });
        this.armExpiry(view);
        return;
      }
      const phase = phaseForError(error);
      if (phase === 'expired') this.settle();
      this.set({ phase, view: null, diagnostic: diagnosticFor(error) });
      return;
    }
    if (generation !== this.generation) return;
    this.inflight = null;
    this.settle();
    const raw = plainObject(data) && exactKeys(data, ['redirect_uri']) ? data.redirect_uri : null;
    const check = checkCallback(raw, decision);
    if (!check.ok || typeof raw !== 'string') {
      this.set({ phase: 'invalid_response', view: null, diagnostic: { code: 'invalid_callback', requestId: null } });
      return;
    }
    this.callback = raw;
    this.forgetTimer = this.setTimer(() => { this.forgetTimer = null; this.forget(); }, CALLBACK_LIFETIME_MS);
    this.set({ phase: check.kind === 'code' ? 'opening' : 'denied', canOpen: true });
    this.open(raw);
  }

  private open(raw: string): void {
    try {
      this.options.open(raw);
    } catch {
      // The explicit "Open Sidetone" button stays available.
    }
  }

  private async load(): Promise<void> {
    this.cancel();
    const generation = this.generation;
    const controller = new AbortController();
    this.inflight = controller;
    this.set({ phase: 'loading', view: null, retryNotice: false, diagnostic: null });
    try {
      const env = await this.options.api.request<DataEnvelope<unknown>>(`/auth/authorize/confirmation?request=${this.options.request}`, { signal: controller.signal });
      if (generation !== this.generation) return;
      const check = checkDetails(plainObject(env) ? env.data : undefined, this.tenantId, this.now());
      if (!check.ok) {
        const code = check.reason === 'invalid' ? 'invalid_response' : check.reason;
        this.set({ phase: code, view: null, diagnostic: { code, requestId: null } });
        return;
      }
      this.set({ phase: 'ready', view: check.view });
      this.armExpiry(check.view);
    } catch (error) {
      if (generation !== this.generation || isAbortError(error)) return;
      this.set({ phase: phaseForError(error), view: null, diagnostic: diagnosticFor(error) });
    } finally {
      if (this.inflight === controller) this.inflight = null;
    }
  }

  private cancel(): void {
    this.generation += 1;
    this.inflight?.abort();
    this.inflight = null;
    this.clearExpiry();
  }

  private settle(): void {
    this.settled = true;
    this.clearExpiry();
  }

  /** Expires the shown request on time, so Allow is never offered for a request the server will refuse. */
  private armExpiry(view: DesktopAuthorizationView): void {
    this.clearExpiry();
    const generation = this.generation;
    const ms = Date.parse(view.expires_at) - this.now();
    this.expiryTimer = this.setTimer(() => {
      this.expiryTimer = null;
      if (generation === this.generation && this.snapshot.phase === 'ready') this.set({ phase: 'expired', view: null, retryNotice: false });
    }, Math.min(Math.max(ms, 0), MAX_TIMER));
  }

  private clearExpiry(): void {
    if (this.expiryTimer !== null) this.clearTimer(this.expiryTimer);
    this.expiryTimer = null;
  }

  private set(patch: Partial<AuthorizeSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...patch };
    for (const l of [...this.listeners]) l();
  }
}

function phaseForError(error: unknown): AuthorizePhase {
  if (!isApiRequestError(error)) return 'error';
  if (error.status === 404 || error.status === 503 || error.code === 'desktop_pkce_unimplemented') return 'unavailable';
  if (error.code === 'authorization_expired' || error.code === 'invalid_request') return 'expired';
  if (error.code === 'tenant_mismatch' || error.code === 'tenant_required') return 'tenant_mismatch';
  if (error.status === 401 || error.code === 'browser_session_required' || error.code === 'mfa_required') return 'reauth';
  return 'error';
}

function diagnosticFor(error: unknown): { code: string; requestId: string | null } {
  return isApiRequestError(error) ? { code: error.code, requestId: error.requestId } : { code: 'unexpected_error', requestId: null };
}
